/*
 * People and access - owner only.
 *
 * The owner adds a person (there is no self sign-up), picks their role, and can
 * hide individual sections from them - "sessions and video but not setups". The
 * person receives a one-time invite link; if email is off, the link is shown
 * here to send by hand. No password is ever chosen for them or shown.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Mail, Plus, Trash2, UserPlus } from 'lucide-react'
import clsx from 'clsx'
import { ApiError, api } from '../lib/api'
import type { ManagedUser } from '../lib/api'

const ROLE_HELP: Record<string, string> = {
  owner: 'Sees and changes everything.',
  editor: 'Records setups and uploads. Deletes nothing.',
  viewer: 'Looks only. Changes nothing.',
}

export function People() {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('editor')
  const [hidden, setHidden] = useState<string[]>([])
  const [invite, setInvite] = useState<{ email: string; url: string; emailed: boolean } | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: ManagedUser[] }>('/api/users'),
  })
  const sectionsQ = useQuery({
    queryKey: ['user-sections'],
    queryFn: () => api.get<{ sections: Record<string, string> }>('/api/users/sections'),
  })
  const sections = sectionsQ.data?.sections ?? {}

  const toggle = (key: string) =>
    setHidden((h) => (h.includes(key) ? h.filter((k) => k !== key) : [...h, key]))

  const create = useMutation({
    mutationFn: () =>
      api.post<ManagedUser & { invite_url: string; invite_emailed: boolean }>(
        '/api/users', { email: email.trim(), name: name.trim(), role, hidden_sections: hidden }),
    onSuccess: (u) => {
      setInvite({ email: u.email, url: u.invite_url, emailed: u.invite_emailed })
      setEmail('')
      setName('')
      setRole('editor')
      setHidden([])
      setErr(null)
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not add them.'),
  })
  const patch = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api.patch(`/api/users/${v.id}`, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
  const resend = useMutation({
    mutationFn: (id: number) =>
      api.post<{ invite_url: string; invite_emailed: boolean; email: string }>(
        `/api/users/${id}/resend-invite`, {}),
    onSuccess: (u) => setInvite({ email: u.email, url: u.invite_url, emailed: u.invite_emailed }),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/api/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const rows = usersQ.data?.users ?? []
  const ownerCount = rows.filter((r) => r.role === 'owner').length

  return (
    <>
      {invite && (
        <div className="card" style={{ marginBottom: 14, borderColor: 'rgba(43,127,255,.45)' }}>
          <div className="card-head">
            <h2 className="row" style={{ gap: 8 }}><Mail size={18} /> Invite for {invite.email}</h2>
          </div>
          <p className="small" style={{ marginTop: 0 }}>
            {invite.emailed
              ? 'Emailed to them. They set their own password from the link.'
              : 'Email is off or bounced — send them this link to set their password:'}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <input readOnly value={invite.url} style={{ fontSize: 13 }}
              onFocus={(e) => e.currentTarget.select()} />
            <button className="btn ghost" onClick={() => navigator.clipboard?.writeText(invite.url)}>
              <Copy size={15} /> Copy
            </button>
          </div>
          <button className="btn ghost small" style={{ marginTop: 12 }}
            onClick={() => setInvite(null)}>Done</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <h2 className="row" style={{ gap: 8 }}><UserPlus size={18} /> Add a person</h2>
        </div>
        <div className="grid" style={{ gap: 10, gridTemplateColumns: '1fr 1fr', maxWidth: 560 }}>
          <div>
            <label className="tiny dim">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="person@example.com" style={{ width: '100%' }} />
          </div>
          <div>
            <label className="tiny dim">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Rachael Stewart" style={{ width: '100%' }} />
          </div>
        </div>
        <div style={{ marginTop: 10, maxWidth: 560 }}>
          <label className="tiny dim">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: '100%' }}>
            <option value="editor">Editor — records setups and uploads</option>
            <option value="viewer">Viewer — looks only</option>
            <option value="owner">Owner — full access</option>
          </select>
          <p className="tiny dim" style={{ marginTop: 6 }}>{ROLE_HELP[role]}</p>
        </div>

        {role !== 'owner' && (
          <div style={{ marginTop: 12 }}>
            <label className="tiny dim">Hide sections from this person (optional)</label>
            <div className="row wrap" style={{ gap: 8, marginTop: 6 }}>
              {Object.entries(sections).map(([key, label]) => (
                <button key={key} type="button"
                  className={clsx('btn small', hidden.includes(key) ? '' : 'ghost')}
                  onClick={() => toggle(key)}>
                  {hidden.includes(key) ? 'Hidden: ' : ''}{label}
                </button>
              ))}
            </div>
            <p className="tiny dim" style={{ marginTop: 6 }}>
              Sessions and video are always visible. Tap a section to hide it — e.g. hide
              Setups so they see times and video but not the setup sheet.
            </p>
          </div>
        )}

        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" disabled={!email.trim() || create.isPending}
            onClick={() => create.mutate()}>
            <Plus size={16} /> {create.isPending ? 'Adding…' : 'Add and send invite'}
          </button>
        </div>
        {err && <div className="advice warn">{err}</div>}
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-head"><h2>People</h2></div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr><th>Person</th><th>Role</th><th>Sees</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const hiddenList = u.hidden_sections || []
                return (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.name || u.email}</div>
                      {u.name && <div className="tiny dim">{u.email}</div>}
                    </td>
                    <td>
                      <select value={u.role}
                        disabled={u.role === 'owner' && ownerCount < 2}
                        onChange={(e) => patch.mutate({ id: u.id, body: { role: e.target.value } })}>
                        <option value="owner">Owner</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </td>
                    <td className="small">
                      {u.role === 'owner' ? <span className="dim">everything</span>
                        : hiddenList.length === 0 ? <span className="dim">all sections</span>
                          : <span>not: {hiddenList.map((k) => sections[k] || k).join(', ')}</span>}
                    </td>
                    <td>
                      <span className={clsx('badge', u.status === 'active' ? 'saved'
                        : u.status === 'invited' ? 'partial' : 'none')}>{u.status}</span>
                      {u.google_linked && (
                        <span className="tiny dim" style={{ marginLeft: 6 }}>Google</span>
                      )}
                    </td>
                    <td className="num">
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {!u.has_password && !u.google_linked && (
                          <button className="btn ghost small" onClick={() => resend.mutate(u.id)}>
                            <Mail size={13} /> Invite
                          </button>
                        )}
                        <button className="btn ghost small" onClick={() => {
                          if (confirm(`Remove ${u.name || u.email}?`)) remove.mutate(u.id)
                        }}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
